import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useNotifications, Notification } from "@/hooks/useNotifications";
import { formatDistanceToNow } from "date-fns";

interface AuditLog {
  id: string;
  user_id: string;
  action_type: string;
  table_name: string;
  created_at: string;
  viewed_by_super_admin: boolean;
}

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [auditUnreadCount, setAuditUnreadCount] = useState(0);
  const [auditNotifications, setAuditNotifications] = useState<AuditLog[]>([]);
  const [open, setOpen] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const { 
    notifications: userNotifications, 
    unreadCount: userUnreadCount,
    markAsRead,
    markAllAsRead 
  } = useNotifications();

  // Check if user is super admin
  useEffect(() => {
    const checkSuperAdmin = async () => {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.user) return;

      const { data } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", session.session.user.id)
        .eq("role", "super_admin")
        .single();

      setIsSuperAdmin(!!data);
    };

    checkSuperAdmin();
  }, []);

  const fetchAuditNotifications = async () => {
    if (!isSuperAdmin) return;

    const { data, error } = await supabase
      .from("audit_logs")
      .select("*")
      .eq("viewed_by_super_admin", false)
      .order("created_at", { ascending: false })
      .limit(50);

    if (!error && data) {
      setAuditNotifications(data);
      setAuditUnreadCount(data.length);
    }
  };

  const markAuditAsViewed = async () => {
    if (auditNotifications.length === 0) return;

    const ids = auditNotifications.map(n => n.id);
    await supabase
      .from("audit_logs")
      .update({ viewed_by_super_admin: true })
      .in("id", ids);

    setAuditUnreadCount(0);
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchAuditNotifications();

      const channel = supabase
        .channel('audit-logs-changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'audit_logs'
          },
          () => {
            fetchAuditNotifications();
          }
        )
        .subscribe();

      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (open && auditUnreadCount > 0 && isSuperAdmin) {
      markAuditAsViewed();
    }
  }, [open, isSuperAdmin]);

  const formatActionType = (action: string) => {
    return action.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const totalUnread = userUnreadCount + (isSuperAdmin ? auditUnreadCount : 0);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read_at) {
      markAsRead(notification.id);
    }
    if (notification.link) {
      navigate(notification.link);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="relative">
          <Bell className="h-4 w-4" />
          {totalUnread > 0 && (
            <Badge 
              className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 bg-destructive text-destructive-foreground"
              variant="destructive"
            >
              {totalUnread > 99 ? '99+' : totalUnread}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="font-semibold">Notifications</h3>
            <p className="text-xs text-muted-foreground">
              {totalUnread > 0 ? `${totalUnread} unread` : 'All caught up!'}
            </p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => navigate('/notifications')}>
            <ExternalLink className="h-3 w-3 mr-1" />
            View all
          </Button>
        </div>

        <Tabs defaultValue="all" className="w-full">
          <TabsList className="grid w-full grid-cols-2 m-2" style={{ width: 'calc(100% - 16px)' }}>
            <TabsTrigger value="all" className="text-xs">
              All ({userNotifications.length + (isSuperAdmin ? auditNotifications.length : 0)})
            </TabsTrigger>
            {isSuperAdmin && (
              <TabsTrigger value="activity" className="text-xs">
                Activity ({auditNotifications.length})
              </TabsTrigger>
            )}
            {!isSuperAdmin && (
              <TabsTrigger value="unread" className="text-xs">
                Unread ({userUnreadCount})
              </TabsTrigger>
            )}
          </TabsList>

          <ScrollArea className="h-80">
            <TabsContent value="all" className="mt-0 p-2 space-y-2">
              {userNotifications.length === 0 && auditNotifications.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No notifications
                </div>
              ) : (
                <>
                  {userNotifications.slice(0, 10).map((notification) => (
                    <Card 
                      key={notification.id} 
                      className={`p-3 cursor-pointer transition-colors hover:bg-accent ${
                        !notification.read_at ? 'border-l-2 border-l-primary' : ''
                      }`}
                      onClick={() => handleNotificationClick(notification)}
                    >
                      <div className="text-sm">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{notification.title}</p>
                          {!notification.read_at && (
                            <Badge variant="secondary" className="text-xs">New</Badge>
                          )}
                        </div>
                        <p className="text-muted-foreground text-xs line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-muted-foreground text-xs mt-1">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </Card>
                  ))}
                  {isSuperAdmin && auditNotifications.slice(0, 5).map((notification) => (
                    <Card key={notification.id} className="p-3 hover:bg-accent transition-colors">
                      <div className="text-sm">
                        <p className="font-medium">
                          {formatActionType(notification.action_type)}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          Table: {notification.table_name}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </Card>
                  ))}
                </>
              )}
            </TabsContent>

            {isSuperAdmin && (
              <TabsContent value="activity" className="mt-0 p-2 space-y-2">
                {auditNotifications.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No recent activity
                  </div>
                ) : (
                  auditNotifications.map((notification) => (
                    <Card key={notification.id} className="p-3 hover:bg-accent cursor-pointer transition-colors">
                      <div className="text-sm">
                        <p className="font-medium">
                          {formatActionType(notification.action_type)}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          Table: {notification.table_name}
                        </p>
                        <p className="text-muted-foreground text-xs">
                          {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                        </p>
                      </div>
                    </Card>
                  ))
                )}
              </TabsContent>
            )}

            {!isSuperAdmin && (
              <TabsContent value="unread" className="mt-0 p-2 space-y-2">
                {userNotifications.filter(n => !n.read_at).length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    No unread notifications
                  </div>
                ) : (
                  userNotifications
                    .filter(n => !n.read_at)
                    .map((notification) => (
                      <Card 
                        key={notification.id} 
                        className="p-3 cursor-pointer transition-colors hover:bg-accent border-l-2 border-l-primary"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="text-sm">
                          <p className="font-medium">{notification.title}</p>
                          <p className="text-muted-foreground text-xs line-clamp-2">
                            {notification.message}
                          </p>
                          <p className="text-muted-foreground text-xs mt-1">
                            {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                          </p>
                        </div>
                      </Card>
                    ))
                )}
              </TabsContent>
            )}
          </ScrollArea>
        </Tabs>

        {userUnreadCount > 0 && (
          <div className="p-2 border-t">
            <Button 
              variant="ghost" 
              size="sm" 
              className="w-full text-xs"
              onClick={() => markAllAsRead()}
            >
              <Check className="h-3 w-3 mr-1" />
              Mark all as read
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};
